import React, { useEffect, useRef, useMemo } from 'react';
import Plotly from 'plotly.js-dist';

interface PnLDistributionChartProps {
  plotlyData: any[];
  plotlyLayout: any;
  width?: number;
  height?: number;
  className?: string;
}

const PnLDistributionChart: React.FC<PnLDistributionChartProps> = ({ 
  plotlyData, 
  plotlyLayout,
  width = 800, 
  height = 400, 
  className = ""
}) => {
  const plotRef = useRef<HTMLDivElement>(null);

  // Plotly 데이터와 레이아웃을 다크 테마에 맞게 조정
  const plotData = useMemo(() => {
    if (!plotlyData || plotlyData.length === 0) return [];

    // API에서 받은 데이터를 그대로 사용하되, 다크 테마에 맞게 조정
    return plotlyData.map(trace => ({
      ...trace,
      // 텍스트 색상을 흰색으로 변경 (다크 테마)
      textfont: {
        ...trace.textfont,
        color: '#FFFFFF'
      },
      // 바 차트의 border 제거
      marker: {
        ...trace.marker,
        line: {
          width: 0 // border 제거
        }
      }
    }));
  }, [plotlyData]);

  // Y축 최대값 계산 (상단 텍스트를 위한 여유 공간 확보)
  const yAxisMax = useMemo(() => {
    if (!plotlyData || plotlyData.length === 0) return undefined;
    
    // 모든 trace의 y 값 중 최대값 찾기
    let maxValue = 0;
    plotlyData.forEach(trace => {
      if (trace.y && Array.isArray(trace.y)) {
        const traceMax = Math.max(...trace.y.map((v: number) => Number(v) || 0));
        maxValue = Math.max(maxValue, traceMax);
      }
    });
    
    // 상단 텍스트를 위한 15% 여유 공간 추가
    return maxValue > 0 ? maxValue * 1.15 : undefined;
  }, [plotlyData]);

  // Plotly 레이아웃을 다크 테마에 맞게 조정
  const layout = useMemo(() => ({
    ...plotlyLayout,
    width: width,
    height: height,
    paper_bgcolor: 'rgb(17, 24, 39)', // 다크 배경
    plot_bgcolor: 'rgb(17, 24, 39)', // 다크 플롯 배경
    font: {
      color: '#9CA3AF', // 회색 텍스트
      size: 12
    },
    title: {
      text: plotlyLayout.title?.text || 'Realized PnL Distribution', // 제목 설정 (API에서 없으면 기본값 사용)
      font: {
        color: '#FFFFFF',
        size: 14
      },
      x: 0, // 왼쪽 정렬
      xanchor: 'left', // 왼쪽 상단에 배치
      y: 1.02, // 상단 여백
      yanchor: 'bottom'
    },
    xaxis: {
      ...plotlyLayout.xaxis,
      gridcolor: '#374151',
      zerolinecolor: '#374151',
      linecolor: '#6B7280',
      tickfont: {
        color: '#fff',
        size: 11
      },
      titlefont: {
        color: '#9CA3AF',
        size: 12
      }
    },
    yaxis: {
      ...plotlyLayout.yaxis,
      gridcolor: '#374151',
      zerolinecolor: '#374151',
      linecolor: '#6B7280',
      tickfont: {
        color: '#9CA3AF',
        size: 10
      },
      titlefont: {
        color: '#9CA3AF',
        size: 12
      },
      // 상단 텍스트가 잘리지 않도록 Y축 범위를 확장
      range: yAxisMax ? [0, yAxisMax] : (plotlyLayout.yaxis?.range ? [
        plotlyLayout.yaxis.range[0],
        plotlyLayout.yaxis.range[1] * 1.15 // 상단에 15% 여유 공간 추가
      ] : undefined)
    },
    legend: {
      ...plotlyLayout.legend,
      orientation: 'h', // 가로 배치
      x: 1, // 
      y: 1.02, // 상단 위
      xanchor: 'right',
      yanchor: 'bottom',
      font: {
        color: '#9CA3AF',
        size: 10
      },
    },
    annotations: [], // annotations 제거 (화살표 등 불필요한 요소 제거)
    margin: {
      l: 50, // 왼쪽 여백 (기본값보다 작게)
      r: 20, // 오른쪽 여백
      t: 50, // 상단 여백 (legend와 텍스트를 위한 공간 확보)
      b: 100  // 하단 여백
    },
  }), [plotlyLayout, width, height, yAxisMax]);



  useEffect(() => {
    if (!plotRef.current || !plotData || plotData.length === 0) return;

    const config = {
      displayModeBar: false,
      displaylogo: false,
      responsive: true,
      toImageButtonOptions: {
        format: 'png',
        filename: 'pnl-distribution-chart',
        height: height,
        width: width,
        scale: 1
      }
    };

    const currentPlotRef = plotRef.current;

    Plotly.newPlot(currentPlotRef, plotData, layout, config);

    // cleanup 함수
    return () => {
      if (currentPlotRef) {
        Plotly.purge(currentPlotRef);
      }
    };
  }, [plotData, layout, width, height]);

  // 리사이즈 이벤트 처리
  useEffect(() => {
    if (!plotRef.current) return;

    const handleResize = () => {
      if (plotRef.current) {
        Plotly.relayout(plotRef.current, {
          width: width,
          height: height
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [width, height]);

  if (!plotlyData || plotlyData.length === 0) {
    return (
      <div className={`flex items-center justify-center bg-gray-800 rounded ${className}`} style={{ width, height }}>
        <p className="text-gray-400 text-sm">데이터가 없습니다.</p>
      </div>
    );
  }

  return (
    <div className={`rounded ${className}`}>
      <div ref={plotRef} style={{ width, height }} />
    </div>
  );
};

export default PnLDistributionChart;

